/**
 * User-timezone time formatting — ALL trade timestamps in the app render in the USER'S profile
 * timezone (the `timeZone` they picked in Settings, the same IANA id the BE uses to bucket the
 * calendar/by-time stats), NOT the browser's. A trader reviewing from a laptop abroad must see the
 * same clock times everywhere (2026-07-08 user directive).
 *
 * The tz is held in a module store (like liveQuotesStore, not React state) so plain formatter
 * functions can read it: `UserTimeZoneSync` (mounted once in the AppShell) writes it from the
 * `/users/me` profile. Fallback order: profile tz → browser tz. An invalid/unknown IANA id falls
 * back to the browser silently (Intl throws RangeError).
 */

let userTimeZone: string | null = null;

/** Set from the profile (`UserTimeZoneSync`). Pass null/'' to clear back to the browser default. */
export function setUserTimeZone(tz: string | null | undefined): void {
  userTimeZone = tz || null;
}

/** The effective display timezone: the profile's when set, else the browser's. */
export function getUserTimeZone(): string | undefined {
  return userTimeZone ?? undefined;
}

function fmt(iso: string | Date, options: Intl.DateTimeFormatOptions): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '-';
  try {
    return d.toLocaleString(undefined, { ...options, timeZone: getUserTimeZone() });
  } catch {
    // Invalid IANA id in the profile — fall back to the browser tz rather than crash.
    return d.toLocaleString(undefined, options);
  }
}

/** Full date + time (e.g. "7/6/2026, 8:59:13 AM") in the user's timezone. */
export function tzDateTime(iso: string | Date): string {
  return fmt(iso, { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' });
}

/** Short time (e.g. "08:59 AM") in the user's timezone — table Opened/Closed/Entry/Exit cells. */
export function tzTime(iso: string | Date): string {
  return fmt(iso, { hour: '2-digit', minute: '2-digit' });
}

/** Short date (e.g. "Jul 6") in the user's timezone — chart axis ticks. */
export function tzDateShort(iso: string | Date): string {
  return fmt(iso, { month: 'short', day: 'numeric' });
}

/** Custom Intl options, still in the user's timezone — for one-off formats (e.g. the Topbar stamp). */
export function tzFormat(iso: string | Date, options: Intl.DateTimeFormatOptions): string {
  return fmt(iso, options);
}

/**
 * UTC midnight of the TRADER's calendar date, as an ISO instant, optionally backed up whole days.
 *
 * Used by the broker catch-up polls (2026-09-14) to say "everything from the start of my business day".
 * CQG's `HistoricalOrdersRequest.from_date` is a BUSINESS DATE, not a timestamp, so only the calendar
 * date carries meaning — taking the trader's date in their profile zone (the same date the calendar and
 * Plan My Day use, via {@link todayKeyInTz}) and returning UTC midnight of it is zone-correct with no
 * offset arithmetic. `daysBack >= 1` covers CQG's ~17:00 CT business-date roll AND keeps the instant in
 * the past for a trader east of UTC. The result is clamped to `now` as a backstop.
 */
// (defined below todayKeyInTz's doc so the two read together)

/**
 * `"YYYY-MM-DD"` of `now` in `tz` — the TRADER's calendar date, not the browser's and not UTC.
 *
 * Plan My Day's whole day model is the user's local date (the lock, the streaks and the two reminder
 * emails are all computed in the profile zone on the backend), so the Topbar deep link, the Calendar's
 * "today" accent and the grid's pending-marks cutoff must agree with it. A trader in Chicago opening
 * the app at 21:00 plans "today" — Sep 9 — even though UTC has already rolled to Sep 10.
 *
 * Fallback order: the given `tz` → the profile zone in the module store → the browser zone. An
 * unknown IANA id (Intl throws `RangeError`) falls back to UTC rather than crashing the page.
 */
export function startOfDayUtcIso(daysBack = 0, now: Date = new Date(), tz?: string | null): string {
  const key = todayKeyInTz(tz, now);
  const ms = Date.UTC(Number(key.slice(0, 4)), Number(key.slice(5, 7)) - 1, Number(key.slice(8, 10)))
    - daysBack * 86_400_000;
  // Never ask a broker for a future instant: a trader east of UTC starts their calendar date BEFORE
  // the current UTC instant reaches it (Tokyo at 2026-09-14T18:40Z is already 2026-09-15 locally).
  return new Date(Math.min(ms, now.getTime())).toISOString();
}

export function todayKeyInTz(tz?: string | null, now: Date = new Date()): string {
  return dateKeyInTz(now, tz);
}

/** `"YYYY-MM-DD"` of any instant in `tz` (e.g. the trader's date of their first fill), with {@link todayKeyInTz}'s fallbacks. */
export function dateKeyInTz(at: Date | number, tz?: string | null): string {
  return dateKeyOf(dateKeyFormat(tz), at);
}

const HOUR_MS = 3_600_000;

/**
 * The instant the trader's calendar date `key` (`YYYY-MM-DD`) BEGINS in `tz`, as an ISO string: the first
 * moment whose date there is `key`. That is local midnight, or the DST change itself where one skips
 * midnight (Beirut, Santiago). Same fallbacks as {@link todayKeyInTz}.
 *
 * The backend buckets the calendar by the date in the profile zone (`/stats/daily`, and Plan My Day's day
 * through `DayLock.UtcBounds`), so a query for "that day" must start here, never at `${key}T00:00:00Z`:
 * UTC midnight is 03:00 in Jerusalem and 20:00 the evening before in New York.
 */
export function startOfDayInTzIso(key: string, tz?: string | null): string {
  const format = dateKeyFormat(tz);
  const utcMidnight = Date.UTC(Number(key.slice(0, 4)), Number(key.slice(5, 7)) - 1, Number(key.slice(8, 10)));
  // Every offset lies within UTC−12..UTC+14, so the date begins within 15 h either side of its UTC midnight:
  // `before` still shows the previous date and `begins` shows `key` or later. Bisect down to the millisecond.
  let before = utcMidnight - 15 * HOUR_MS;
  let begins = utcMidnight + 15 * HOUR_MS;
  while (begins - before > 1) {
    const mid = before + Math.floor((begins - before) / 2);
    if (dateKeyOf(format, mid) >= key) begins = mid;
    else before = mid;
  }
  return new Date(begins).toISOString();
}

/**
 * The last millisecond of the trader's date `key` in `tz` (the next date's start, minus 1 ms): the inclusive
 * end the global date filter sends as `dateTo`. Same fallbacks as {@link todayKeyInTz}.
 */
export function endOfDayInTzIso(key: string, tz?: string | null): string {
  const next = new Date(Date.UTC(Number(key.slice(0, 4)), Number(key.slice(5, 7)) - 1, Number(key.slice(8, 10)) + 1));
  return new Date(Date.parse(startOfDayInTzIso(next.toISOString().slice(0, 10), tz)) - 1).toISOString();
}

/** A `YYYY-MM-DD` formatter for the given `tz` → the profile zone → the browser zone; an unknown id → UTC. */
function dateKeyFormat(tz?: string | null): Intl.DateTimeFormat {
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
  try {
    return new Intl.DateTimeFormat('en-CA', { ...options, timeZone: tz || getUserTimeZone() || undefined });
  } catch {
    return new Intl.DateTimeFormat('en-CA', { ...options, timeZone: 'UTC' });
  }
}

function dateKeyOf(format: Intl.DateTimeFormat, at: Date | number): string {
  const parts = format.formatToParts(at);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find(p => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}
